/**
 * @file Helper library for retrieving country-specific data.
 * @description This file contains functions to get currency, tax, and country code information.
 */

// We assume this function is defined elsewhere and handles authentication/data loading.
import { loadAuth } from "./authMgmt.js";

/**
 * A constant mapping of country names to their data, including taxes and currency.
 * This refactors the original getData function to a static object for efficiency.
 */
export const COUNTRY_DATA = {
    "Singapore": {
      countryCode: "+65",
      phoneFormatIntl: "+65 XXXX XXXX", // 8 digits
      intlDigits: 8,
      intlRegex: /^\+65\d{8}$/,
  
      "taxes": [
        { "Name": "Goods and Services Tax", "short_name": "GST", "amount": 0.09 }
      ],
      "currency": { "name": "Singapore Dollar", "short_name": "SGD" },
      normalizeValues(value) {
        // Default normalization for currencies that use two decimal places (e.g., SGD, PHP, THB, AED, HKD, MYR, AUD)
        return Number(value).toFixed(2);
      }
    },
    "Philippines": {
      countryCode: "+63",
      phoneFormatIntl: "+63 9XX XXX XXXX", // drop trunk 0
      intlDigits: 10,                       // '9' + 9 digits
      intlRegex: /^\+639\d{9}$/,
  
      "taxes": [
        { "Name": "Value Added Tax", "short_name": "VAT", "amount": 0.12 }
      ],
      "currency": { "name": "Philippine Peso", "short_name": "PHP" },
      "noCard": true,
      normalizeValues(value) {
        // Default normalization for currencies that use two decimal places (e.g., SGD, PHP, THB, AED, HKD, MYR, AUD)
        return Number(value).toFixed(2);
      }
    },
    "Thailand": {
      countryCode: "+66",
      phoneFormatIntl: "+66 8/9X XXX XXXX", // mobiles start 8/9
      intlDigits: 9,
      intlRegex: /^\+66[89]\d{8}$/,
  
      "taxes": [
        { "Name": "Value Added Tax", "short_name": "VAT", "amount": 0.07 }
      ],
      "currency": { "name": "Thai Baht", "short_name": "THB" },
      "noCard": true,
      normalizeValues(value) {
        // Default normalization for currencies that use two decimal places (e.g., SGD, PHP, THB, AED, HKD, MYR, AUD)
        return Number(value).toFixed(2);
      }
    },
    "United Arab Emirates": {
      countryCode: "+971",
      phoneFormatIntl: "+971 5X XXX XXXX",
      intlDigits: 9,
      intlRegex: /^\+9715\d{8}$/,
  
      "taxes": [
        { "Name": "Value Added Tax", "short_name": "VAT", "amount": 0.05 }
      ],
      "currency": { "name": "United Arab Emirates Dirham", "short_name": "AED" },
      "noCard": true,
      normalizeValues(value) {
        // Default normalization for currencies that use two decimal places (e.g., SGD, PHP, THB, AED, HKD, MYR, AUD)
        return Number(value).toFixed(2);
      }
    },
    "Vietnam": {
      countryCode: "+84",
      phoneFormatIntl: "+84 9X XXX XXXX",
      intlDigits: 9,
      intlRegex: /^\+849\d{8}$/,
  
      "taxes": [
        { "Name": "Value Added Tax", "short_name": "VAT", "amount": 0.10 }
      ],
      "currency": { "name": "Vietnamese Dong", "short_name": "VND" },
      "noCard": true,
      normalizeValues(value) {
        // Vietnamese Dong (VND) typically does not use decimal places.
        // We round it to the nearest whole number.
        return Math.round(Number(value)).toFixed(0);
      }
    }, 
    "Japan": {
      countryCode: "+81",
      phoneFormatIntl: "+81 7/8/9X XXXX XXXX", // mobiles 70/80/90 → drop 0
      intlDigits: 10,
      intlRegex: /^\+81[789]\d{8}$/,
  
      "taxes": [
        { "Name": "Consumption Tax", "short_name": "JCT", "amount": 0.10 }
      ],
      "currency": { "name": "Japanese Yen", "short_name": "JPY" },
      normalizeValues(value) {
        // Japanese Yen (JPY) does not use decimal places.
        // We round it to the nearest whole number.
        return Math.round(Number(value)).toFixed(0);
      }
    },
    "Hong Kong": {
      countryCode: "+852",
      phoneFormatIntl: "+852 XXXX XXXX",
      intlDigits: 8,
      intlRegex: /^\+852\d{8}$/,
  
      "taxes": [],
      "currency": { "name": "Hong Kong Dollar", "short_name": "HKD" },
      normalizeValues(value) {
        // Default normalization for currencies that use two decimal places (e.g., SGD, PHP, THB, AED, HKD, MYR, AUD)
        return Number(value).toFixed(2);
      }
    },
    "Malaysia": {
      countryCode: "+60",
      phoneFormatIntl: "+60 1X XXX XXXX(X)", // 9–10 digits after +60
      intlDigitsRange: [9, 10],
      intlRegex: /^\+601\d{8,9}$/,
  
      "taxes": [
        { "Name": "Sales and Service Tax", "short_name": "SST", "amount": 0.06 }
      ],
      "currency": { "name": "Malaysian Ringgit", "short_name": "MYR" },
      "noCard": true,
      normalizeValues(value) {
        // Default normalization for currencies that use two decimal places (e.g., SGD, PHP, THB, AED, HKD, MYR, AUD)
        return Number(value).toFixed(2);
      }
    },
    "Australia": {
      countryCode: "+61",
      phoneFormatIntl: "+61 4XX XXX XXX", // mobiles 04 → +61 4
      intlDigits: 9,
      intlRegex: /^\+614\d{8}$/,
  
      "taxes": [
        { "Name": "Goods and Services Tax", "short_name": "GST", "amount": 0.10 }
      ],
      "currency": { "name": "Australian Dollar", "short_name": "AUD" },
      normalizeValues(value) {
        // Default normalization for currencies that use two decimal places (e.g., SGD, PHP, THB, AED, HKD, MYR, AUD)
        return Number(value).toFixed(2);
      }
    }
  };


export const SupportedCountry = (key) => key in COUNTRY_DATA;

/**
* A constant mapping of currency short names to their symbols.
*/
const CURRENCY_SYMBOLS = {
  "USD": "$",  // US Dollar
  "CAD": "$",  // Canadian Dollar
  "EUR": "€",  // Euro
  "GBP": "£",  // British Pound
  "JPY": "¥",  // Japanese Yen
  "AUD": "$",  // Australian Dollar
  "CHF": "CHF", // Swiss Franc
  "NZD": "$",  // New Zealand Dollar
  "SGD": "S$", // Singapore Dollar
  "HKD": "$",  // Hong Kong Dollar
  "MYR": "RM",  // Malaysian Ringgit
  "THB": "฿",
  "PHP": "₱"
};
const COUNTRY_CODES = {
    "Philippines": "PH",
    "Thailand": "TH",
    "United Arab Emirates": "AE",
    "Vietnam": "VN",
    "United States": "US",
    "Canada": "CA",
    
    "Singapore": "SG",
    "Japan": "JP",
    "Hong Kong": "HK",
    "Malaysia": "MY",
    "Australia": "AU",
};


/**
 * Retrieves all the country data.
 * @returns {object} An object containing all country data.
 */
export function getData() {
    return COUNTRY_DATA;
}

export function getCurrentData() {
    return COUNTRY_DATA[loadAuth("company")?.country ?? "Singapore"];
}

/**
 * Retrieves the currency code for the currently loaded company.
 * @returns {string} The currency code (e.g., "USD").
 */
export function getCurrency() {
    return loadAuth("company")?.currency ?? "USD";
}

/**
 * Retrieves the currency symbol for the current currency.
 * @returns {string} The currency symbol (e.g., "$") or the currency code if not found.
 */
export function getCurrencySymbol() {
    return CURRENCY_SYMBOLS[getCurrency()] || getCurrency();
}

/**
 * Retrieves the tax information for the currently loaded company's country.
 * @returns {Array<object>} An array of tax objects.
 */
export function getTaxes() {
    return COUNTRY_DATA[loadAuth("company")?.country ?? "Singapore"]?.taxes ?? [];
}

export function getIfCardUnavailable(){
  return COUNTRY_DATA[loadAuth("company")?.country ?? "Singapore"]?.noCard ?? false;
}

/**
 * Retrieves the two-letter country code for a given country name.
 * This is useful for Stripe Express connected account creation.
 * @param {string} countryName - The full name of the country (e.g., "United States").
 * @returns {string | undefined} The two-letter country code or undefined if not found.
 */
export function getCountryCode(countryName) {
    return COUNTRY_CODES[countryName];
}
